import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Heading6, BodyRegular, BodyLargeBold } from '@src/fonts/typography/Typography';
import { DarkTextColor } from '@src/fonts/typography/TypographyColors';
import { LayoutColors } from '@src/core/theme';
import useTranslate from '@src/i18n/useTranslate';
import InputCode from '@src/components/Auth/InputComponents/InputCode';
import FilledButton from '@src/components/reusable/button/FilledButton';
import { darken } from 'polished';
import DotFalling from '@src/components/reusable/spinner/DotFalling';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import {
  buyerSelector,
  errorsSelector,
  phoneVerificationSelector,
  requestPhoneVerification,
  verifyPhone,
} from '@src/features/auth';
import { IMask } from 'react-imask';
import useCountdownTimer from '@src/hooks/UseCountDown';
import { BRAZIL_PHONE_DDI } from '@src/features/order';
import { getErrorMessage } from '@src/common';
import useAppEnvironment from '@src/hooks/useAppEnvironment';
import { masks } from '@src/common/masks';

type PhoneVerificationProps = {
  onBack?: () => void;
  onSuccess?: () => void;
  onFail?: () => void;
  showBackButton?: boolean;
};

const PhoneVerificationForm = (props: PhoneVerificationProps) => {
  const { onBack = () => {}, onSuccess = () => {}, onFail = () => {}, showBackButton = true } = props;

  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const appEnv = useAppEnvironment();

  const [buyer] = useAppSelector(buyerSelector);
  const phoneVerification = useAppSelector(phoneVerificationSelector);
  const errors = useAppSelector(errorsSelector);

  const { minutes, seconds } = useCountdownTimer(
    phoneVerification.requestLockedUntil ? new Date(phoneVerification.requestLockedUntil) : new Date(),
  );

  const [code, setCode] = useState('');

  const sendCode = () => {
    dispatch(requestPhoneVerification());
  };

  const verify = () => {
    dispatch(verifyPhone({ code }));
  };

  const getMaskedPhone = (phone: string) => {
    const phoneMasker = IMask.createMask({
      mask: masks['brazilian_phone'],
    });

    if (phone.startsWith(BRAZIL_PHONE_DDI)) {
      return phoneMasker.resolve(phone);
    }

    return phone;
  };

  const getFormattedTime = (time: number) => {
    return time.toLocaleString('pt-BR', { minimumIntegerDigits: 2, useGrouping: false });
  };

  const errorMessage = getErrorMessage(errors['phone_verification'], 'request');

  useEffect(() => {
    if (appEnv === 'production') {
      sendCode();
    }
  }, []);

  useEffect(() => {
    switch (phoneVerification.status) {
      case 'success':
        onSuccess();
        break;
      case 'error':
        onFail();
        break;

      default:
        break;
    }
  }, [phoneVerification.status]);

  return (
    <>
      <Title>{i18n('auth_verify_phone_form_title')}</Title>
      <Subtitle>
        {i18n('auth_verify_phone_form_subtitle')}
        <br />
        <strong>{getMaskedPhone(buyer!.phone!)}</strong>
      </Subtitle>

      <CodeInput
        label={i18n('auth_verify_phone_form_code')}
        errorContext={'phone_verification'}
        setCodeOTP={(e) => setCode(e)}
        codeOTP={code}
      />

      {phoneVerification.requestLockedUntil &&
      new Date() < new Date(phoneVerification.requestLockedUntil) &&
      (minutes == 0 || seconds == 0) ? (
        <CountDownText>
          {i18n('auth_verify_phone_form_code_expires_in')} {getFormattedTime(minutes)}:{getFormattedTime(seconds)}
        </CountDownText>
      ) : (
        <ResendSmsText>
          {i18n('auth_verify_phone_form_did_not_receive')}
          {phoneVerification.loading ? (
            '...'
          ) : (
            <ResendSmsButton onClick={sendCode}>{i18n('auth_verify_phone_form_resend')}</ResendSmsButton>
          )}
        </ResendSmsText>
      )}

      <Error>{errorMessage}</Error>

      <Button
        disabled={phoneVerification.loading}
        bgColor={LayoutColors.whiteLight}
        textColor={LayoutColors.warningDark}
        onClick={() => verify()}
      >
        {phoneVerification.loading ? <DotFalling /> : i18n('auth_verify_phone_form_submit')}
      </Button>

      {showBackButton ? (
        <PreviousButton onClick={() => onBack()}>{i18n('auth_verify_phone_form_back')}</PreviousButton>
      ) : null}
    </>
  );
};

export const Error = styled.div`
  ${BodyRegular}
  margin-bottom: 24px;
  color: ${LayoutColors.errorDark};
`;

export const Title = styled.h4`
  ${Heading6}
  ${DarkTextColor}
  margin-bottom: 24px;
`;

export const Subtitle = styled.p`
  ${BodyRegular}
  color: ${LayoutColors.blackMain};
  margin-bottom: 24px;
`;

export const CodeInput = styled(InputCode)`
  margin-bottom: 24px;
`;

const ResendSmsText = styled.div`
  ${BodyRegular};
  display: block;
  width: 100%;
  margin-bottom: 24px;
`;

const CountDownText = styled.div`
  ${BodyRegular};
  display: block;
  width: 100%;
  margin-bottom: 24px;
`;

const ResendSmsButton = styled.a`
  font-size: inherit;
  color: ${LayoutColors.justBlue};
  padding-left: 5px;
  text-decoration: none;
  cursor: pointer;
`;

export const Button = styled(FilledButton)`
  color: ${LayoutColors.whiteLight};
  background-color: ${LayoutColors.successDark};
  text-transform: uppercase;
  &:hover {
    background-color: ${darken(0.1, LayoutColors.successDark)};
  }
  margin-bottom: 8px;
`;

export const PreviousButton = styled.a`
  ${BodyLargeBold};
  cursor: pointer;
  text-decoration: none;
  display: block;
  text-align: center;
`;

export default PhoneVerificationForm;
